import { useMemo } from 'react';

import { useGetAutomationRevision } from '@/generated/hooks/grimoire/v1/api';
import type { V1Automation } from '@/generated/hooks/grimoire/models';
import { captureException } from '@/modules/sentry';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { isTokenFetchError } from '@/shared/utils/error-handler';
import { decodeBase64ToText } from '@/shared/utils/base64-text';
import {
  AOP_ARTIFACT_NAME,
  BLUEPRINT_ARTIFACT_NAME,
} from '@/modules/rain/agents/quill';

/**
 * Revisions are immutable once created, so a fetched snapshot never goes stale
 * within a session. The long `gcTime` is what keeps stepping through version
 * history smooth: a revision viewed once renders instantly from cache on the
 * way back, with no skeleton.
 */
const REVISION_STALE_TIME_MS = 5 * 60 * 1000;
const REVISION_GC_TIME_MS = 60 * 60 * 1000;

const EMPTY_CONTENT: IRevisionContent = {};

/**
 * The displayable content of a single automation revision.
 */
export interface IRevisionContent {
  /** The revision's automation description, if it had one. */
  description?: string;
  /** The revision's AOP text — see {@link resolveRevisionCode}. */
  englishCode?: string;
}

/**
 * Parameters for {@link useRevisionContents}.
 */
export interface IUseRevisionContentsParams {
  /** The automation the revisions belong to. */
  automationId: string;
  /** Bare revision ID of the selected version (e.g. "2.0"), without the "v". */
  revisionId?: string;
  /**
   * Bare revision ID of the version immediately before the selected one — the
   * diff baseline. Undefined for the oldest revision, which has no predecessor.
   */
  previousRevisionId?: string;
}

/**
 * Return type for {@link useRevisionContents}.
 */
export interface IUseRevisionContentsResult {
  /** Content of the selected revision. */
  selected: IRevisionContent;
  /** Content of the selected revision's predecessor. */
  previous: IRevisionContent;
  /**
   * True until *both* requested revisions have resolved. Callers must gate diff
   * rendering on this: if only the selected side has landed, the baseline is
   * still empty and the diff would briefly read as "everything was added".
   */
  isLoading: boolean;
  /** True when either side failed to load. */
  isError: boolean;
}

function decodeArtifact(
  artifacts: Record<string, string> | undefined,
  name: string,
): string | undefined {
  const base64 = artifacts?.[name];
  if (!base64) return undefined;
  try {
    return decodeBase64ToText(base64);
  } catch {
    return undefined;
  }
}

/**
 * Resolves the SOP/AOP text a revision should display in version history.
 *
 * `english_code` is the canonical AOP, but it isn't always projected into a
 * revision snapshot. Both `aop.md` (the built AOP) and `blueprint/aop.md` (the
 * pre-build requirements) are NON-reserved artifacts, so they live in the
 * snapshot's `artifacts` map. Prefer the built AOP (`aop.md`) over the blueprint
 * so a built revision shows its AOP — falling back to `blueprint/aop.md` only
 * leaves the blueprint-stage requirements showing, which is why going
 * blueprint → AOP appeared to render nothing new.
 */
export function resolveRevisionCode(
  snapshot: V1Automation | undefined,
): string | undefined {
  return (
    snapshot?.english_code ||
    decodeArtifact(snapshot?.artifacts, AOP_ARTIFACT_NAME) ||
    decodeArtifact(snapshot?.artifacts, BLUEPRINT_ARTIFACT_NAME)
  );
}

/**
 * Fetches the displayable content of a selected revision and its predecessor.
 *
 * The version-history list itself is built from `ListAutomationRevisionSummaries`,
 * whose snapshots deliberately omit `code` and `description` to keep list
 * responses small enough for the API gateway's gRPC-JSON transcoder. Content for
 * the one or two revisions actually on screen is therefore pulled per revision
 * via `GetAutomationRevision`.
 *
 * Both sides are fetched in parallel, which makes stepping *down* the list
 * nearly free: selecting v3 fetches v3 and v2, so selecting v2 next finds it
 * already cached and only fetches v1.
 *
 * @param params - The automation and the two revision IDs to fetch
 * @returns Both revisions' content plus combined loading/error state
 */
export function useRevisionContents({
  automationId,
  revisionId,
  previousRevisionId,
}: IUseRevisionContentsParams): IUseRevisionContentsResult {
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const canFetch = Boolean(
    automationId && currentOrganizationId && currentWorkspaceId,
  );

  const queryOptions = (revision: string | undefined) => ({
    query: {
      enabled: Boolean(canFetch && revision),
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: REVISION_STALE_TIME_MS,
      gcTime: REVISION_GC_TIME_MS,
      meta: {
        onError: (err: Error) => {
          // Token fetch errors are already captured in token-manager.ts;
          // skipping here prevents duplicate Sentry events.
          if (isTokenFetchError(err)) return;
          captureException(err, {
            tags: {
              module: 'automation-editor',
              action: 'get_revision_content',
            },
          });
        },
      },
    },
  });

  const selectedQuery = useGetAutomationRevision(
    currentOrganizationId,
    currentWorkspaceId,
    automationId,
    revisionId || '',
    queryOptions(revisionId),
  );

  const previousQuery = useGetAutomationRevision(
    currentOrganizationId,
    currentWorkspaceId,
    automationId,
    previousRevisionId || '',
    queryOptions(previousRevisionId),
  );

  const selected = useMemo(
    () => toRevisionContent(selectedQuery.data?.data.snapshot),
    [selectedQuery.data?.data.snapshot],
  );

  const previous = useMemo(
    () => toRevisionContent(previousQuery.data?.data.snapshot),
    [previousQuery.data?.data.snapshot],
  );

  return {
    selected,
    previous,
    // `isLoading` (not `isPending`) — a disabled query stays pending forever,
    // so the oldest revision would never finish "loading" its predecessor.
    isLoading: selectedQuery.isLoading || previousQuery.isLoading,
    isError: selectedQuery.isError || previousQuery.isError,
  };
}

function toRevisionContent(
  snapshot: V1Automation | undefined,
): IRevisionContent {
  if (!snapshot) return EMPTY_CONTENT;
  return {
    description: snapshot.description,
    englishCode: resolveRevisionCode(snapshot),
  };
}
